import { Fragment, useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Flag, MoreHorizontal, Pencil, Pin, Reply, Smile, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/components/feedback/toast';
import type { MemberRole, ReactionRow, ViewMember, ViewMessage } from '../api/communityView';
import { type Channel, channelIcon, dayLabel } from './channels';
import { avatarBg, avatarInitial, displayName, formatTime } from './cvHelpers';
import { EMOJI_SET, summarizeReactions } from './reactions';
import { Composer } from './Composer';

interface Props {
  channel: Channel;
  messages: ViewMessage[];
  members: ViewMember[];
  reactions: ReactionRow[];
  roleOf: (id: string) => MemberRole;
  currentUserId: string | null;
  isOwner: boolean;
  signedIn: boolean;
  joined: boolean;
  sending: boolean;
  typing: string[];
  onSend: (body: string, opts?: { media?: string | null; replyToId?: string | null }) => void;
  onUploadImage: (file: File) => Promise<string>;
  onToggleReaction: (messageId: string, emoji: string) => void;
  onTyping: () => void;
  onEdit: (id: string, body: string) => void;
  onDelete: (id: string) => void;
  onRequireJoin: () => void;
  onOpenProfile: (id: string, anchor?: DOMRect) => void;
  onReport: (postId: string) => void;
  /** Optional pin toggle. When provided, a Pin button appears in the hover
   *  toolbar (used by DMs; community chat leaves it off). */
  onTogglePin?: (message: ViewMessage, pinned: boolean) => void;
  pinnedIds?: Set<string>;
  hideHeader?: boolean;
}

// Hover toolbar + reaction popover buttons (OG .cv2-msg-hover button / .cv2-react-pop button).
const HOVER_BTN =
  'grid h-[30px] w-[30px] cursor-pointer place-items-center rounded-[7px] border-0 bg-transparent text-white/[0.72] transition-all duration-[120ms] hover:bg-white/[0.08] hover:text-[#fafafa]';
const REACT_BTN =
  'h-[30px] w-[30px] cursor-pointer rounded-[7px] border-0 bg-transparent text-base leading-none hover:bg-white/[0.08]';

function renderText(text: string): ReactNode {
  return text.split(/(@[\w_-]+)/g).map((part, i) =>
    part.startsWith('@') ? (
      <span className="rounded-[6px] bg-[rgba(124,92,252,0.18)] px-1.5 py-px font-semibold text-[#c4b5fd]" key={i}>
        {part}
      </span>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

type Item =
  | { type: 'divider'; key: string; label: string }
  | { type: 'msg'; key: string; msg: ViewMessage; first: boolean };

function buildItems(messages: ViewMessage[]): Item[] {
  const items: Item[] = [];
  let lastDay = '';
  let prevAuthor = '';
  let prevTime = 0;
  for (const m of messages) {
    const day = dayLabel(m.created_at);
    const t = new Date(m.created_at).getTime();
    let first = false;
    if (day !== lastDay) {
      items.push({ type: 'divider', key: `d-${m.id}`, label: day });
      lastDay = day;
      prevAuthor = '';
      first = true;
    }
    // A reply always starts a fresh block so its quote header is visible.
    if (m.author_id !== prevAuthor || t - prevTime > 7 * 60 * 1000 || m.reply_to_id) first = true;
    items.push({ type: 'msg', key: m.id, msg: m, first });
    prevAuthor = m.author_id;
    prevTime = t;
  }
  return items;
}

export function ChatPane({
  channel,
  messages,
  members,
  reactions,
  roleOf,
  currentUserId,
  isOwner,
  signedIn,
  joined,
  sending,
  typing,
  onSend,
  onUploadImage,
  onToggleReaction,
  onTyping,
  onEdit,
  onDelete,
  onRequireJoin,
  onOpenProfile,
  onReport,
  onTogglePin,
  pinnedIds,
  hideHeader = false,
}: Props) {
  const Icon = channelIcon(channel);
  const canPost = signedIn && joined && !channel.readOnly;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [reactFor, setReactFor] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<ViewMessage | null>(null);

  // Lookups: parent message for reply quotes, and reactions grouped by message.
  const byId = new Map(messages.map((m) => [m.id, m]));
  const reactionsByMsg = new Map<string, ReactionRow[]>();
  for (const r of reactions) {
    const list = reactionsByMsg.get(r.message_id);
    if (list) list.push(r);
    else reactionsByMsg.set(r.message_id, [r]);
  }

  function react(id: string, emoji: string) {
    onToggleReaction(id, emoji);
    setReactFor(null);
  }
  function startEdit(m: ViewMessage) {
    setEditingId(m.id);
    setEditDraft(m.body ?? '');
  }
  function saveEdit() {
    if (editingId) onEdit(editingId, editDraft.trim());
    setEditingId(null);
  }
  function handleSend(body: string, media?: string | null) {
    onSend(body, { media: media ?? null, replyToId: replyTarget?.id ?? null });
    setReplyTarget(null);
  }

  const items = buildItems(messages);

  // Always open on the freshest message: pin the scroll to the bottom when the
  // channel changes or new messages arrive (useLayoutEffect → no top-flash).
  const scrollRef = useRef<HTMLDivElement>(null);
  const latestMessageId = messages[messages.length - 1]?.id;
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);
  // Pin to bottom only when the viewer is already near it (so loading a historic
  // image while scrolled up doesn't yank them down) — used by image onLoad.
  const pinIfNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 260) el.scrollTop = el.scrollHeight;
  }, []);
  // Depend on the message *count* too: sending appends a row whose height isn't
  // known until layout, so we re-pin on raf + a short timeout fallback.
  useLayoutEffect(() => {
    scrollToBottom();
    const frame = window.requestAnimationFrame(scrollToBottom);
    const timer = window.setTimeout(scrollToBottom, 120);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [channel.id, messages.length, latestMessageId, scrollToBottom]);

  return (
    <>
      {!hideHeader && (
        <div className="flex h-14 flex-none items-center gap-3.5 border-b border-white/[0.05] px-[18px]">
          <span className="inline-flex items-center text-white/[0.46]">
            <Icon size={18} strokeWidth={2} />
          </span>
          <span className="text-[15px] font-extrabold tracking-[-0.2px] text-[#fafafa]">{channel.name}</span>
        </div>
      )}

      <div ref={scrollRef} className="chat-scroll min-h-0 flex-1 overflow-y-auto pb-2 pt-4">
        {messages.length < 3 && (
          <div className="px-6 pb-3.5 pt-6 text-center text-[13px] text-white/[0.46] [&_b]:font-bold [&_b]:text-white/[0.72]">
            This is the beginning of <b>#{channel.name}</b>.
          </div>
        )}

        {items.map((item) => {
          if (item.type === 'divider') {
            return (
              <div
                className="flex items-center gap-3 px-6 pb-1.5 pt-3.5 text-[11px] font-bold uppercase tracking-[0.8px] text-white/[0.46] before:h-px before:flex-1 before:bg-white/[0.05] before:content-[''] after:h-px after:flex-1 after:bg-white/[0.05] after:content-['']"
                key={item.key}
              >
                <span>{item.label}</span>
              </div>
            );
          }
          const m = item.msg;
          const name = displayName(m.author);
          const role = roleOf(m.author_id);
          const own = currentUserId != null && m.author_id === currentUserId;
          const canDelete = own || isOwner;
          const parent = m.reply_to_id ? byId.get(m.reply_to_id) : undefined;
          const summary = summarizeReactions(reactionsByMsg.get(m.id) ?? [], currentUserId);

          return (
            <div
              className={cn(
                'group relative flex gap-3.5 px-6 py-1 transition-[background] duration-[120ms] hover:bg-white/[0.025]',
                item.first && 'mt-0.5 pt-3.5',
              )}
              id={`cv2-msg-${m.id}`}
              key={item.key}
            >
              <div
                className={cn(
                  'grid h-[38px] w-[38px] flex-none cursor-pointer place-items-center self-start rounded-full bg-white/10 bg-cover bg-center text-sm font-extrabold text-white',
                  !item.first && 'invisible',
                )}
                style={avatarBg(m.author?.avatar_url)}
                onClick={(e) => onOpenProfile(m.author_id, e.currentTarget.getBoundingClientRect())}
              >
                {!m.author?.avatar_url && avatarInitial(name)}
              </div>
              <div className="min-w-0 flex-1">
                {/* Reply quote — the message this one is replying to. */}
                {m.reply_to_id && (
                  <button
                    type="button"
                    className="mb-0.5 flex max-w-full items-center gap-1.5 text-left text-[12px] text-white/[0.46] transition-colors hover:text-white/[0.72]"
                    onClick={() => {
                      const el = document.getElementById(`cv2-msg-${m.reply_to_id}`);
                      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                  >
                    <Reply size={12} className="flex-none -scale-x-100" />
                    <span className="font-semibold text-white/[0.72]">{displayName(parent?.author)}</span>
                    <span className="truncate">
                      {parent ? parent.body || (parent.media ? 'attachment' : '') : 'message unavailable'}
                    </span>
                  </button>
                )}

                <div className={cn('mb-0.5 items-baseline gap-2', item.first ? 'flex' : 'hidden')}>
                  <span className="cursor-pointer text-sm font-bold tracking-[-0.1px] text-[#fafafa]" onClick={(e) => onOpenProfile(m.author_id, e.currentTarget.getBoundingClientRect())}>
                    {name}
                  </span>
                  {role !== 'MEMBER' && (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-pill px-[7px] py-0.5 text-[9.5px] font-extrabold uppercase tracking-[0.5px]',
                        role === 'ADMIN' ? 'bg-white/[0.06] text-white/[0.72]' : 'bg-white/[0.08] text-[#fafafa]',
                      )}
                    >
                      {role}
                    </span>
                  )}
                  <span className="text-[11px] text-white/[0.46]">{formatTime(m.created_at)}</span>
                </div>

                {editingId === m.id ? (
                  <>
                    <textarea
                      className="w-full resize-none rounded-[10px] border border-white/10 bg-white/[0.04] px-2.5 py-2 text-[14.5px] leading-[1.5] text-[#fafafa] outline-none"
                      rows={2}
                      value={editDraft}
                      autoFocus
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          saveEdit();
                        }
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                    <div className="mt-1.5 flex gap-2 text-[11px] text-white/[0.46]">
                      <button className="cursor-pointer border-0 bg-transparent text-xs font-semibold text-accent" onClick={saveEdit}>
                        Save
                      </button>
                      <button className="cursor-pointer border-0 bg-transparent text-xs font-semibold text-white/[0.72]" onClick={() => setEditingId(null)}>
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {m.body && (
                      <div className="whitespace-pre-wrap break-words text-[14.5px] leading-[1.55] text-white/[0.72]">
                        {renderText(m.body)}
                        {m.edited_at && <span className="ml-1.5 text-[10.5px] italic text-white/[0.46]">(edited)</span>}
                      </div>
                    )}
                    {m.mediaUrl && (
                      <a href={m.mediaUrl} target="_blank" rel="noreferrer" className="mt-1.5 inline-block">
                        <img className="block max-h-[280px] max-w-[380px] cursor-zoom-in rounded-xl border border-white/10" src={m.mediaUrl} alt="" loading="lazy" onLoad={pinIfNearBottom} />
                      </a>
                    )}
                    {summary.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {summary.map((s) => (
                          <button
                            key={s.emoji}
                            className={cn(
                              'inline-flex cursor-pointer items-center gap-[5px] rounded-pill border px-[9px] py-[3px] text-xs font-semibold transition-all duration-[120ms]',
                              s.mine
                                ? 'border-[#54b9fe80] bg-[#54b9fe29] text-[#fafafa]'
                                : 'border-white/10 bg-white/[0.05] text-white/[0.72] hover:border-white/20',
                            )}
                            onClick={() => onToggleReaction(m.id, s.emoji)}
                          >
                            {s.emoji} {s.count}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {reactFor === m.id && (
                <div className="absolute -top-[42px] right-6 z-[5] flex gap-0.5 rounded-[10px] border border-white/10 bg-[rgba(20,20,26,0.98)] p-1 shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
                  {EMOJI_SET.map((e) => (
                    <button className={REACT_BTN} key={e} onClick={() => react(m.id, e)}>
                      {e}
                    </button>
                  ))}
                </div>
              )}

              <div
                className={cn(
                  "absolute right-6 z-[2] hidden gap-px rounded-[10px] border border-white/10 bg-[rgba(20,20,26,0.96)] p-[3px] shadow-[0_10px_30px_rgba(0,0,0,0.5)] group-hover:flex",
                  item.first ? '-top-1' : '-top-4',
                )}
              >
                <button className={HOVER_BTN} title="React" onClick={() => setReactFor((c) => (c === m.id ? null : m.id))}>
                  <Smile size={16} />
                </button>
                <button
                  className={HOVER_BTN}
                  title="Reply"
                  onClick={() => {
                    if (!canPost) {
                      onRequireJoin();
                      return;
                    }
                    setReplyTarget(m);
                  }}
                >
                  <Reply size={16} />
                </button>
                {own && (
                  <button className={HOVER_BTN} title="Edit" onClick={() => startEdit(m)}>
                    <Pencil size={16} />
                  </button>
                )}
                {canDelete && (
                  <button
                    className={HOVER_BTN}
                    title="Delete"
                    onClick={() => {
                      if (window.confirm('Delete this message?')) onDelete(m.id);
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                {!own && (
                  <button className={HOVER_BTN} title="Report" onClick={() => onReport(m.id)}>
                    <Flag size={16} />
                  </button>
                )}
                {onTogglePin && (() => {
                  const pinned = pinnedIds?.has(m.id) ?? false;
                  return (
                    <button
                      className={cn(HOVER_BTN, pinned && 'text-accent')}
                      title={pinned ? 'Unpin' : 'Pin'}
                      onClick={() => onTogglePin(m, pinned)}
                    >
                      <Pin size={16} className={pinned ? 'fill-current' : ''} />
                    </button>
                  );
                })()}
                <button className={HOVER_BTN} title="More" onClick={() => toast('More actions coming soon')}>
                  <MoreHorizontal size={16} />
                </button>
              </div>
            </div>
          );
        })}

        {!messages.length && (
          <div className="px-6 pb-3.5 text-center text-[13px] text-white/[0.46] [&_b]:font-bold [&_b]:text-white/[0.72]" style={{ paddingTop: 40 }}>
            No messages in <b>#{channel.name}</b> yet.
          </div>
        )}
      </div>

      {typing.length > 0 && (
        <div className="flex min-h-[22px] flex-none items-center gap-2 px-6 pb-1.5 pt-1 text-xs text-white/[0.46]">
          <span className="inline-flex gap-[3px]">
            <i className="h-[5px] w-[5px] animate-cv2-bounce rounded-full bg-[#39ff14] shadow-[0_0_6px_rgba(57,255,20,0.5)]" />
            <i className="h-[5px] w-[5px] animate-cv2-bounce rounded-full bg-[#39ff14] shadow-[0_0_6px_rgba(57,255,20,0.5)] [animation-delay:0.15s]" />
            <i className="h-[5px] w-[5px] animate-cv2-bounce rounded-full bg-[#39ff14] shadow-[0_0_6px_rgba(57,255,20,0.5)] [animation-delay:0.3s]" />
          </span>
          {typing.length === 1 ? `${typing[0]} is typing…` : `${typing.length} people are typing…`}
        </div>
      )}

      {channel.readOnly ? (
        <div className="pointer-events-none relative mx-[22px] mb-[18px] mt-1 flex-none rounded-md border border-white/10 bg-white/[0.03] opacity-60 backdrop-blur-[8px]">
          <div className="flex items-end gap-1.5 py-2 pl-2.5 pr-2">
            <div className="flex-1 px-1 py-2 text-[14.5px] text-[#fafafa]">🔒 #{channel.name} is read-only.</div>
          </div>
        </div>
      ) : !canPost ? (
        <div className="relative mx-[22px] mb-[18px] mt-1 flex-none cursor-pointer rounded-md border border-white/10 bg-white/[0.03] backdrop-blur-[8px]" onClick={onRequireJoin}>
          <div className="flex items-end gap-1.5 py-2 pl-2.5 pr-2">
            <div className="flex-1 px-1 py-2 text-[14.5px] text-white/[0.46]">Join this community to send messages…</div>
          </div>
        </div>
      ) : (
        <Composer
          channel={channel}
          members={members}
          sending={sending}
          replyTo={
            replyTarget
              ? { name: displayName(replyTarget.author), excerpt: replyTarget.body || (replyTarget.media ? 'attachment' : '') }
              : null
          }
          onCancelReply={() => setReplyTarget(null)}
          onUploadImage={onUploadImage}
          onSend={handleSend}
          onTyping={onTyping}
        />
      )}
    </>
  );
}
